import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader.jsx';
import Card from '../components/Card.jsx';
import { TextField, SelectField, Options, Cell } from '../components/Field.jsx';
import { Loader, ErrorAlert, EmptyRow } from '../components/States.jsx';
import useForm from '../hooks/useForm.js';
import useRowEditor from '../hooks/useRowEditor.js';
import {
  useListStockOutQuery,
  useCreateStockOutMutation,
  useUpdateStockOutMutation,
  useDeleteStockOutMutation,
  useCreateCustomerMutation,
  useLazyLookupPriceQuery,
  useGetStockAvailabilityQuery,
} from '../store/apiSlice.js';
import { stockOutSchema, stockOutLineSchema, customerSchema } from '../validation/schemas.js';
import { validateRowSet, remapRowErrors, rowError } from '../validation/rules.js';
import { useToast } from '../context/ToastContext.jsx';
import { useReferenceData } from '../store/useReferenceData.js';
import { fmtDate, today, money } from '../utils/format.js';
import { downloadCsv } from '../utils/csv.js';

const NEW_CUSTOMER = '__new__';
const SKU_FIELDS = ['product', 'texture', 'color', 'length'];
const GST_RATES = [0, 3, 5, 12, 18, 28];

const isBlank = (value) => value === '' || value === null || value === undefined;
const toNumberOrNull = (value) => (isBlank(value) ? null : Number(value));
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const skuKey = (row) => [row.product, row.texture, row.length, row.color].map((v) => String(v ?? '').trim()).join('||');

/**
 * Same maths as the server's priceInvoiceLine: price is GST inclusive, so the
 * tax is carved out of the line total. What you see here is what gets saved.
 */
function lineMoney(row) {
  const qty = Number(row.qty) || 0;
  const price = Number(row.price) || 0;
  const gst = Number(row.gst) || 0;
  const total = round2(price * qty);
  const taxableValue = round2(total / (1 + gst / 100));
  return { total, taxableValue, gstAmount: round2(total - taxableValue) };
}

/**
 * `priceNote` says where the price in the box came from:
 *   ''          — nothing picked yet
 *   'list'      — suggested from Price Management (still editable)
 *   'nolist'    — no Price Management entry; type one
 *   'manual'    — typed by the operator; never overwritten by a lookup
 */
const blankRow = () => ({
  product: '', texture: '', color: '', length: '', qty: '',
  price: '', gst: '', priceNote: '', priceLoading: false,
});

const BLANK_CUSTOMER = { company: '', address: '', phone: '', gst: '', contact: '', customerType: 'Standard' };

export default function StockOutPage() {
  const showToast = useToast();
  const { products, textures, lengths, colors, customers, reload: reloadReferenceData } = useReferenceData();
  const { data: invoices, isLoading: loading, error: listError, refetch: reload } = useListStockOutQuery();
  // Stock levels are a convenience. If they fail to load, say so — never treat
  // "couldn't read" as "0 in stock". The server re-checks stock on save anyway.
  const { data: availability, isSuccess: stockLoaded, isError: stockFailed } = useGetStockAvailabilityQuery();
  const error = listError?.message ?? null;

  const [createStockOut] = useCreateStockOutMutation();
  const [updateStockOut] = useUpdateStockOutMutation();
  const [deleteStockOut] = useDeleteStockOutMutation();
  const [createCustomer] = useCreateCustomerMutation();
  const [lookupPrice] = useLazyLookupPriceQuery();

  const form = useForm({ customer: '', date: today() }, stockOutSchema);
  const newCustomerForm = useForm(BLANK_CUSTOMER, customerSchema);
  const [rowErrors, setRowErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const { rows, addRow, removeRow, patchRow, resetRows, setRows } = useRowEditor(blankRow);

  // One counter per row: a slow lookup can never overwrite a newer one.
  const lookupSeq = useRef({});

  const customer = form.values.customer;
  const showNewCustomerForm = customer === NEW_CUSTOMER;
  const selectedCustomer = customers.find((c) => c.company === customer);

  /**
   * Units on the shelf per SKU. While correcting an invoice, its own units are
   * added back — they are "sold" in the ledger but are being re-sold by this edit.
   */
  const onShelf = useMemo(() => {
    const map = new Map();
    for (const row of availability || []) map.set(skuKey(row), row.available);
    for (const line of editingInvoice?.lines || []) {
      map.set(skuKey(line), (map.get(skuKey(line)) ?? 0) + line.qty);
    }
    return map;
  }, [availability, editingInvoice]);

  const skuComplete = (row) => SKU_FIELDS.every((field) => row[field]);

  /** Suggest price and GST from Price Management — only into boxes the operator hasn't typed in. */
  async function suggestPrice(key, row, forCustomer) {
    if (row.priceNote === 'manual') return;
    if (!skuComplete(row) || !forCustomer || forCustomer === NEW_CUSTOMER) {
      patchRow(key, { price: '', gst: '', priceNote: '', priceLoading: false });
      return;
    }
    const seq = (lookupSeq.current[key] = (lookupSeq.current[key] || 0) + 1);
    patchRow(key, { priceLoading: true });
    try {
      const found = await lookupPrice({
        product: row.product, texture: row.texture, color: row.color, length: row.length, customer: forCustomer,
      }).unwrap();
      if (seq !== lookupSeq.current[key]) return;
      patchRow(key, found
        ? { price: found.price ?? '', gst: found.gst ?? '', priceNote: 'list', priceLoading: false }
        : { price: '', gst: '', priceNote: 'nolist', priceLoading: false });
    } catch {
      if (seq === lookupSeq.current[key]) patchRow(key, { priceNote: 'nolist', priceLoading: false });
    }
  }

  const clearCellError = (index, field) => {
    setRowErrors((current) => {
      const cell = `lines.${index}.${field}`;
      if (!current[cell]) return current;
      const next = { ...current };
      delete next[cell];
      return next;
    });
  };

  const editCell = (row, index, field, value) => {
    if (field === 'price' || field === 'gst') {
      // Typing in either box makes the line manual; a later lookup won't touch it.
      patchRow(row._key, { [field]: value, priceNote: 'manual' });
    } else if (SKU_FIELDS.includes(field)) {
      // A different item: forget the old price unless it was typed by hand.
      const next = { ...row, [field]: value };
      patchRow(row._key, { [field]: value });
      suggestPrice(row._key, next, customer);
    } else {
      patchRow(row._key, { [field]: value });
    }
    clearCellError(index, field);
  };

  // Switching customer can change the suggested price on every untouched row.
  useEffect(() => {
    if (!customer || customer === NEW_CUSTOMER) return;
    rows.forEach((row) => suggestPrice(row._key, row, customer));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  const err = (index, field) => rowError(rowErrors, 'lines', index, field);

  const totals = rows.reduce(
    (sum, row) => {
      const m = lineMoney(row);
      return { taxable: sum.taxable + m.taxableValue, gst: sum.gst + m.gstAmount, total: sum.total + m.total };
    },
    { taxable: 0, gst: 0, total: 0 }
  );

  const saveNewCustomer = async () => {
    if (!newCustomerForm.validate()) return;
    setBusy(true);
    try {
      const created = await createCustomer(newCustomerForm.values).unwrap();
      await reloadReferenceData();
      form.setValue('customer', created.company);
      newCustomerForm.reset(BLANK_CUSTOMER);
      showToast(`Customer added: ${created.company}`);
    } catch (apiError) {
      newCustomerForm.applyServerErrors(apiError.details);
      showToast(apiError.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  /** Stock check before the round trip. Lines for the same SKU share the shelf. */
  function stockErrors(payload, keep) {
    const errors = {};
    if (!stockLoaded) return errors; // unknown stock: let the server decide
    const used = new Map();
    payload.forEach((row, index) => {
      if (!skuComplete(row) || !(Number(row.qty) > 0)) return;
      const key = skuKey(row);
      const left = (onShelf.get(key) ?? 0) - (used.get(key) ?? 0);
      if (Number(row.qty) > left) {
        errors[`lines.${keep[index]}.qty`] = `Only ${Math.max(left, 0)} in stock`;
      }
      used.set(key, (used.get(key) ?? 0) + Number(row.qty));
    });
    return errors;
  }

  const save = async () => {
    if (customer === NEW_CUSTOMER) {
      showToast('Save the new customer first, or pick an existing one', 'error');
      return;
    }
    const headerOk = form.validate();
    const { errors, payload, keep } = validateRowSet(rows, stockOutLineSchema, { key: 'lines' });

    if (!payload.length) {
      setRowErrors({});
      showToast('Fill at least one row with a product and quantity', 'error');
      return;
    }

    const allErrors = { ...stockErrors(payload, keep), ...errors };
    setRowErrors(allErrors);
    if (!headerOk || Object.keys(allErrors).length) {
      showToast('Please fix the highlighted fields', 'error');
      return;
    }

    setBusy(true);
    try {
      const lines = payload.map((row) => ({
        product: row.product,
        texture: row.texture,
        color: row.color,
        length: row.length,
        qty: Number(row.qty),
        // Whatever is in the boxes is what gets billed. Blank → server falls back to Price Management, then 0.
        price: toNumberOrNull(row.price),
        gst: toNumberOrNull(row.gst),
      }));

      if (editingInvoice) {
        const { invoice, repairedRows } = await updateStockOut({
          id: editingInvoice.id,
          payload: { ...form.values, lines },
        }).unwrap();
        showToast(
          repairedRows
            ? `${invoice.invoiceNo} corrected — ₹${money(invoice.grandTotal)}, ${repairedRows} later record(s) recalculated`
            : `${invoice.invoiceNo} corrected — ₹${money(invoice.grandTotal)}`
        );
      } else {
        const invoice = await createStockOut({ ...form.values, lines }).unwrap();
        showToast(`${invoice.invoiceNo} saved — ₹${money(invoice.grandTotal)}`);
      }
      cancelEdit();
    } catch (apiError) {
      const details = apiError.details || {};
      setRowErrors(remapRowErrors(details, 'lines', keep));
      form.applyServerErrors(details);
      showToast(apiError.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  /** Load an invoice back into the form. Its saved prices are kept exactly as billed. */
  const startEdit = (invoice) => {
    setEditingInvoice(invoice);
    form.fill({ customer: invoice.customer, date: invoice.date });
    setRows(
      invoice.lines.map((line, index) => ({
        _key: `edit-${invoice.id}-${index}`,
        product: line.product,
        texture: line.texture,
        color: line.color,
        length: line.length,
        qty: String(line.qty),
        price: isBlank(line.price) ? '' : String(line.price),
        gst: isBlank(line.gst) ? '' : String(line.gst),
        priceNote: 'manual',
        priceLoading: false,
      }))
    );
    setRowErrors({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingInvoice(null);
    form.reset({ customer: '', date: today() });
    resetRows();
    setRowErrors({});
  };

  const handleDelete = async (invoice) => {
    if (!window.confirm(`Delete ${invoice.invoiceNo}? The stock will be returned to inventory.`)) return;
    setBusy(true);
    try {
      await deleteStockOut(invoice.id).unwrap();
      if (editingInvoice?.id === invoice.id) cancelEdit();
      showToast('Invoice deleted, stock returned');
    } catch (apiError) {
      showToast(apiError.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    const csvRows = (invoices || []).flatMap((invoice) =>
      invoice.lines.map((line, index) => ({ invoice, line, lineNo: index + 1 }))
    );
    downloadCsv('stock-out', [
      { label: 'Date', value: ({ invoice }) => invoice.date },
      { label: 'Invoice No', value: ({ invoice }) => invoice.invoiceNo },
      { label: 'Customer', value: ({ invoice }) => invoice.customer },
      { label: 'Customer Type', value: ({ invoice }) => invoice.customerType || 'Standard' },
      { label: 'Line No', value: ({ lineNo }) => lineNo },
      { label: 'Product', value: ({ line }) => line.product },
      { label: 'Texture', value: ({ line }) => line.texture },
      { label: 'Length', value: ({ line }) => line.length },
      { label: 'Color', value: ({ line }) => line.color },
      { label: 'Qty', value: ({ line }) => line.qty },
      { label: 'Price (incl. GST)', value: ({ line }) => line.price ?? 0 },
      { label: 'GST %', value: ({ line }) => line.gst ?? 0 },
      { label: 'Taxable Value', value: ({ line }) => line.taxableValue ?? 0 },
      { label: 'GST Amount', value: ({ line }) => line.gstAmount ?? 0 },
      { label: 'Line Total', value: ({ line }) => line.total ?? 0 },
      { label: 'Previous Stock', value: ({ line }) => line.previousStock ?? '' },
      { label: 'Remaining Stock', value: ({ line }) => line.remainingStock ?? '' },
      { label: 'Invoice Grand Total', value: ({ invoice }) => invoice.grandTotal },
      { label: 'Invoice GST Total', value: ({ invoice }) => invoice.totalGst },
      { label: 'Created At', value: ({ invoice }) => invoice.createdAt || '' },
      { label: 'Last Updated', value: ({ invoice }) => invoice.updatedAt || '' },
    ], csvRows);
  };

  const priceHint = (row) => {
    if (row.priceLoading) return 'Checking price list…';
    if (row.priceNote === 'list') return 'From price list';
    if (row.priceNote === 'nolist') return 'No list price — type one';
    if (row.priceNote === 'manual') return 'Manual';
    return '';
  };

  const stockHint = (row) => {
    if (!skuComplete(row)) return '';
    if (!stockLoaded) return stockFailed ? 'Stock unavailable' : 'Checking stock…';
    const left = onShelf.get(skuKey(row)) ?? 0;
    return left > 0 ? `In stock: ${left}` : 'Out of stock';
  };

  const dateProps = form.fieldProps('date');
  const hint = { color: 'var(--muted)', fontSize: '10.5px', marginTop: 2, whiteSpace: 'nowrap' };

  return (
    <>
      <PageHeader
        eyebrow="Stock"
        title="Stock Out / Invoice"
        subtitle="Select a customer once, then keep adding products. Type the selling price and GST % for each item — the price list only fills in a suggestion."
      />

      <div className="selector-bar">
        <SelectField label="🛒 Selling To (Customer)" {...form.fieldProps('customer')}>
          <option value="">Select customer</option>
          {customers.map((c) => (
            <option key={c.company} value={c.company}>
              {c.company} {c.customerType === 'Regular' ? '· Regular' : ''}
            </option>
          ))}
          <option value={NEW_CUSTOMER}>+ New Customer</option>
        </SelectField>
        <div className="field" style={{ maxWidth: 190 }}>
          <label htmlFor="so-date">
            Invoice Date<span className="req" aria-hidden="true"> *</span>
          </label>
          <input
            id="so-date"
            type="date"
            max={today()}
            className={dateProps.error ? 'invalid' : undefined}
            value={form.values.date}
            onChange={dateProps.onChange}
            onBlur={dateProps.onBlur}
          />
          {dateProps.error && <div className="field-error">{dateProps.error}</div>}
        </div>
        <div style={{ alignSelf: 'flex-end', color: 'var(--muted)', fontSize: '11.5px', paddingBottom: 2 }}>
          Price is per unit and <strong>includes GST</strong>. Taxable value and GST are worked out from it.
        </div>
      </div>

      {showNewCustomerForm && (
        <Card title="New Customer Details" tag="Quick Add">
          <div className="form-grid cols-3">
            <TextField label="Customer Name" placeholder="Customer name" maxLength={80} {...newCustomerForm.fieldProps('company')} />
            <TextField label="Contact Person" placeholder="Full name" maxLength={60} {...newCustomerForm.fieldProps('contact')} />
            <TextField label="Phone" placeholder="+91 99999 12345" {...newCustomerForm.fieldProps('phone')} />
            <TextField label="GST Number" placeholder="Optional" maxLength={15} {...newCustomerForm.fieldProps('gst')} />
            <SelectField label="Customer Type" {...newCustomerForm.fieldProps('customerType')}>
              <option value="Standard">Standard Customer</option>
              <option value="Regular">Regular Customer</option>
            </SelectField>
            <TextField label="Address" placeholder="Street, city" maxLength={200} {...newCustomerForm.fieldProps('address')} />
          </div>
          <div className="btn-row">
            <button type="button" className="btn ghost" onClick={saveNewCustomer} disabled={busy}>
              Save &amp; Select Customer
            </button>
          </div>
        </Card>
      )}

      <Card
        title={editingInvoice ? 'Correct Invoice' : 'Product Rows'}
        tag={editingInvoice ? editingInvoice.invoiceNo : 'Items Being Sold'}
      >
        {editingInvoice && (
          <div className="editing-bar">
            <span>
              Correcting {editingInvoice.invoiceNo}. The invoice number stays the same; prices are saved exactly as shown.
            </span>
            <button type="button" className="action-btn" onClick={cancelEdit}>
              Cancel
            </button>
          </div>
        )}
        <div style={{ overflowX: 'auto' }}>
          <table className="entry-table">
            <thead>
              <tr>
                <th>Product *</th>
                <th>Texture *</th>
                <th>Color *</th>
                <th>Length *</th>
                <th>Qty *</th>
                <th>Price ₹ (incl. GST)</th>
                <th>GST %</th>
                <th>Taxable (₹)</th>
                <th>GST (₹)</th>
                <th>Total (₹)</th>
                <th style={{ width: 36 }} aria-label="Remove row" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const m = lineMoney(row);
                return (
                  <tr key={row._key}>
                    {SKU_FIELDS.map((field) => (
                      <Cell key={field} error={err(index, field)}>
                        <select
                          value={row[field]}
                          onChange={(e) => editCell(row, index, field, e.target.value)}
                          className={err(index, field) ? 'invalid' : undefined}
                          aria-label={field}
                        >
                          <Options
                            items={{ product: products, texture: textures, color: colors, length: lengths }[field]}
                            placeholder={field}
                          />
                        </select>
                      </Cell>
                    ))}
                    <Cell error={err(index, 'qty')}>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        placeholder="0"
                        value={row.qty}
                        onChange={(e) => editCell(row, index, 'qty', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        className={err(index, 'qty') ? 'invalid' : undefined}
                        aria-label="Quantity"
                      />
                      {stockHint(row) && <div style={hint}>{stockHint(row)}</div>}
                    </Cell>
                    <Cell error={err(index, 'price')}>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        placeholder={row.priceLoading ? '…' : '0.00'}
                        value={row.price}
                        onChange={(e) => editCell(row, index, 'price', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        className={err(index, 'price') ? 'invalid' : undefined}
                        aria-label="Price per unit including GST"
                      />
                      {priceHint(row) && <div style={hint}>{priceHint(row)}</div>}
                    </Cell>
                    <Cell error={err(index, 'gst')}>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        inputMode="decimal"
                        list="gst-rates"
                        placeholder="0"
                        value={row.gst}
                        onChange={(e) => editCell(row, index, 'gst', e.target.value)}
                        onWheel={(e) => e.currentTarget.blur()}
                        className={err(index, 'gst') ? 'invalid' : undefined}
                        aria-label="GST %"
                        style={{ maxWidth: 80 }}
                      />
                    </Cell>
                    <td className="calc">{money(m.taxableValue)}</td>
                    <td className="calc">{money(m.gstAmount)}</td>
                    <td className="calc strong">{money(m.total)}</td>
                    <td>
                      <button
                        type="button"
                        className="row-remove"
                        onClick={() => removeRow(row._key)}
                        disabled={rows.length === 1}
                        aria-label="Remove row"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <datalist id="gst-rates">
            {GST_RATES.map((rate) => (
              <option key={rate} value={rate} />
            ))}
          </datalist>
        </div>

        <div style={{ textAlign: 'right', marginTop: 10, fontSize: 12, color: 'var(--muted)' }}>
          Taxable ₹{money(totals.taxable)} · GST ₹{money(totals.gst)}
        </div>
        <div className="grand-total-line">Invoice Total: ₹{money(totals.total)}</div>

        <div className="btn-row">
          <button type="button" className="btn ghost" onClick={addRow}>
            + Add Row
          </button>
          <button type="button" className="btn primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : editingInvoice ? 'Update Invoice' : 'Save Invoice'}
          </button>
        </div>
      </Card>

      <Card title="Stock Out History" tag="Invoices">
        <div className="table-toolbar">
          <button type="button" className="btn ghost" onClick={exportCsv} disabled={!invoices?.length}>
            Export CSV
          </button>
        </div>
        <ErrorAlert message={error} onRetry={reload} />
        {loading ? (
          <Loader />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Invoice No.</th>
                  <th>Customer</th>
                  <th>Items</th>
                  <th>Units Sold</th>
                  <th>GST (₹)</th>
                  <th>Grand Total (₹)</th>
                  <th style={{ width: 80 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {!invoices?.length ? (
                  <EmptyRow colSpan={8}>Save your first invoice above</EmptyRow>
                ) : (
                  invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td className="nowrap">{fmtDate(invoice.date)}</td>
                      <td className="nowrap">{invoice.invoiceNo}</td>
                      <td>{invoice.customer}</td>
                      <td className="num">{invoice.lines.length}</td>
                      <td className="num">{invoice.lines.reduce((sum, line) => sum + line.qty, 0)}</td>
                      <td className="num">₹{money(invoice.totalGst)}</td>
                      <td className="num">₹{money(invoice.grandTotal)}</td>
                      <td>
                        <div className="row-actions">
                          <Link className="action-btn" to={`/invoice/${invoice.id}`}>
                            Invoice
                          </Link>
                          <button type="button" className="action-btn" onClick={() => startEdit(invoice)} disabled={busy}>
                            Edit
                          </button>
                          <button type="button" className="action-btn delete" onClick={() => handleDelete(invoice)} disabled={busy}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}